import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppButton from '../../../../shared/components/app-button/AppButton';
import CruiseLayout, {
  CruiseBackLink,
} from '../../components/common/CruiseLayout';
import CruiseItinerary from '../../components/CruiseItinerary/CruiseItinerary';
import CruiseCategories, {
  tooSmallFor,
} from '../../components/CruiseCategories/CruiseCategories';
import { cruiseCategoriesApi, cruiseItineraryApi } from '../../api/cruiseApi';
import { CRUISE_ROUTES } from '../../constants/cruiseConstants';
import { flowUpTo, resultsUrl, useCruiseFlow } from '../../utils/cruiseFlow';
import placeholder from '../../assets/cruise-placeholder.jpg';
import {
  guestCount,
  listFrom,
  cruiseTitle,
  fareTypeLabel,
  formatAmount,
  formatDateRange,
  isRoundTrip,
  itineraryStats,
  nightsOf,
  plural,
  titleCase,
  wholePartyTotal,
} from '../../utils/cruiseUtils';

const TAB_ITINERARY = 'itinerary';
const TAB_CATEGORIES = 'categories';

function CruiseDetailsPage() {
  const navigate = useNavigate();
  const flow = useCruiseFlow();
  const { cruise, fare, search } = flow;

  const [tab, setTab] = useState(
    flow.category ? TAB_CATEGORIES : TAB_ITINERARY,
  );
  const [stops, setStops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCode, setSelectedCode] = useState(
    flow.category?.categoryCode || '',
  );
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!cruise || !fare) {
      navigate(CRUISE_ROUTES.LANDING, { replace: true });
      return undefined;
    }
    const controller = new AbortController();
    const req = { fareSource: cruise.fareSource, bookingKey: fare.bookingKey };
    setLoading(true);
    Promise.all([
      cruiseItineraryApi(req, controller.signal),
      cruiseCategoriesApi(
        {
          ...req,
          wheelchairAccessible: Boolean(search?.wheelchairAccessible),
        },
        controller.signal,
      ),
    ]).then(([itin, cats]) => {
      if (controller.signal.aborted) return;
      setStops(itin.ok ? listFrom(itin.data, 'itinerary', 'stops') : []);
      const list = cats.ok ? listFrom(cats.data, 'categories') : [];
      setCategories(list);
      // Only categories whose staterooms hold the whole party can be picked.
      const party = guestCount(search || {});
      const fits = list.filter((c) => !tooSmallFor(c, party));
      setSelectedCode((code) => {
        if (code && fits.some((c) => c.categoryCode === code)) return code;
        // Default to the category the search price was quoted for.
        const quoted = fits.find((c) => c.categoryCode === fare.categoryCode);
        return quoted ? quoted.categoryCode : fits[0]?.categoryCode || '';
      });
      if (!cats.ok) {
        setAlert({
          variant: 'error',
          title: 'Cabin categories',
          description: cats.network
            ? 'Could not reach the server. Please try again.'
            : cats.message || 'Could not load cabin categories.',
        });
      }
      setLoading(false);
    });
    return () => controller.abort();
  }, []);

  if (!cruise || !fare) return null;

  const stats = itineraryStats(stops);
  const goResults = () => navigate(resultsUrl(search));

  const handleContinue = () => {
    const category = categories.find((c) => c.categoryCode === selectedCode);
    if (!category) return;
    navigate(CRUISE_ROUTES.CABIN, {
      state: flowUpTo(flow, 'fare', { category }),
    });
  };

  return (
    <CruiseLayout
      step="details"
      loading={loading}
      alert={alert}
      onAlertHide={() => setAlert(null)}
    >
      <CruiseBackLink label="Back to results" onClick={goResults} />

      <div className="cr-hero">
        <img className="cr-hero__img" src={placeholder} alt="" />
        <div className="cr-hero__body">
          <div className="cr-meta">
            {[cruise.cruiseLineName, cruise.shipName]
              .filter(Boolean)
              .join(' · ')}
          </div>
          <h1 className="cr-hero__title">{cruiseTitle(cruise.cruiseName)}</h1>
          <div className="cr-facts">
            <div>
              <span>Duration</span>
              <b>{nightsOf(cruise)} Nights</b>
            </div>
            <div>
              <span>Sailing dates</span>
              <b>{formatDateRange(cruise.startDate, cruise.endDate)}</b>
            </div>
            <div>
              <span>{isRoundTrip(cruise) ? 'Departs & returns' : 'Route'}</span>
              <b>
                {isRoundTrip(cruise)
                  ? titleCase(cruise.startPortName)
                  : `${titleCase(cruise.startPortName)} → ${titleCase(
                      cruise.endPortName,
                    )}`}
              </b>
            </div>
            {stops.length > 0 && (
              <div>
                <span>Ports of call</span>
                <b>
                  {plural(stats.ports, 'port')} ·{' '}
                  {plural(stats.seaDays, 'sea day')}
                </b>
              </div>
            )}
          </div>
          <div className="cr-hero__foot">
            <div>
              <div className="cr-muted-sm">
                {fareTypeLabel(fare.fareType)} · from, whole party
              </div>
              <div className="cr-amount cr-amount--lg">
                {formatAmount(wholePartyTotal(fare))}
              </div>
            </div>
            <AppButton
              btnText="Choose Cabin Category"
              onClick={() => setTab(TAB_CATEGORIES)}
              style={{ height: '42px', padding: '0 22px', fontWeight: 600 }}
            />
          </div>
        </div>
      </div>

      <div className="cr-tabcard">
        <div className="cr-tabs" role="tablist">
          {[
            [TAB_ITINERARY, 'Itinerary'],
            [TAB_CATEGORIES, 'Cabin categories'],
          ].map(([key, label]) => (
            <button
              type="button"
              role="tab"
              key={key}
              aria-selected={tab === key}
              className={`cr-tabs__tab ${tab === key ? 'is-active' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === TAB_ITINERARY && (
          <div className="cr-tab-panel">
            {!loading && <CruiseItinerary stops={stops} />}
          </div>
        )}
      </div>

      {tab === TAB_CATEGORIES && !loading && (
        <CruiseCategories
          categories={categories}
          partySize={guestCount(search || {})}
          selectedCode={selectedCode}
          onSelect={(c) => setSelectedCode(c.categoryCode)}
          onContinue={handleContinue}
        />
      )}
    </CruiseLayout>
  );
}

export default CruiseDetailsPage;
